export const SETTLING_TIME = 10000; // plenty long enough
const MIN_DECAY_MILLISECONDS = 0.001;
export const DECAY_MILLISECONDS = 50;

export class Damper {
	private velocity = 0;
	private naturalFrequency = 0;

	constructor(decayMilliseconds: number = DECAY_MILLISECONDS) {
		this.setDecayTime(decayMilliseconds);
	}

	setDecayTime(decayMilliseconds: number) {
		this.naturalFrequency =
			1 / Math.max(MIN_DECAY_MILLISECONDS, decayMilliseconds);
	}

	update(
		x: number,
		xGoal: number,
		timeStepMilliseconds: number,
		xNormalization: number,
	): number {
		const nilSpeed = 0.0002 * this.naturalFrequency;

		if (x == null || xNormalization === 0) {
			return xGoal;
		}
		if (x === xGoal && this.velocity === 0) {
			return xGoal;
		}
		if (timeStepMilliseconds < 0) {
			return x;
		}
		// Exact solution to a critically damped second-order system, where:
		// acceleration = this.naturalFrequency * this.naturalFrequency * (xGoal
		// - x) - 2 * this.naturalFrequency * this.velocity;
		const deltaX = x - xGoal;
		const intermediateVelocity = this.velocity + this.naturalFrequency * deltaX;
		const intermediateX = deltaX + timeStepMilliseconds * intermediateVelocity;
		const decay = Math.exp(-this.naturalFrequency * timeStepMilliseconds);
		const newVelocity =
			(intermediateVelocity - this.naturalFrequency * intermediateX) * decay;
		const acceleration =
			-this.naturalFrequency * (newVelocity + intermediateVelocity * decay);
		if (
			Math.abs(newVelocity) < nilSpeed * Math.abs(xNormalization) &&
			acceleration * deltaX >= 0
		) {
			// This ensures the controls settle and stop calling this function instead
			// of asymptotically approaching their goal.
			this.velocity = 0;
			return xGoal;
		}
		this.velocity = newVelocity;
		return xGoal + intermediateX * decay;
	}
}
